import React from 'react';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import GraphQLClient from '@api/graphql/GraphQLClient';
import { orderUpdateLogs, orderUpdateLogs_orderUpdateLogs } from '@api/graphql/types';
import * as Queries from '@api/graphql/queries';
import { ApolloQueryResult } from 'apollo-client';
import { difference, flattenObject } from '@root/utils/Object';
import { Loading } from '@components/Loading';
import Notification from 'components/Notification';
import { isNull } from 'util';
import AdminUserLink from '@components/AdminUserLink';
import Paths from '@root/utils/Path';

interface Props {
    id: number;
}

interface HistoryType {
    createdAt: string | null;
    modifiedKey: string;
    modifiedField: string | null;
    modifiedFrom: {} | null;
    modifiedTo: {} | null;
    modifiedBy: string | JSX.Element | null;
    modifierAdmin: string | null;
}

interface StateType {
    loading: boolean;
    history: HistoryType[] | null;
}

type PropType = Props & InjectedIntlProps;

class History extends React.Component<PropType, StateType> {
    public constructor(props: PropType) {
        super(props);

        this.state = {
            loading: false,
            history: null,
        };
    }

    public async componentDidMount(): Promise<void> {
        try {
            this.setState({ loading: true });
            const response: ApolloQueryResult<orderUpdateLogs> = await GraphQLClient.query<orderUpdateLogs>({
                query: Queries.orderUpdateLogs,
                variables: { order_id: this.props.id },
            });
            if (response.data && response.data.orderUpdateLogs && response.data.orderUpdateLogs.length > 0) {
                const history = this.createHistory(response.data.orderUpdateLogs);
                this.setState({ history }, () => this.setState({ loading: false }));
            }
        } catch (error) {
            Notification.error(this.props.intl.formatMessage({id: 'notification.failed'}));
        } finally {
            this.setState({ loading: false });
        }
    }

    private createHistory(logs: orderUpdateLogs_orderUpdateLogs[]): HistoryType[] | null {
        const rows: HistoryType[] = [];
        logs.forEach((log: orderUpdateLogs_orderUpdateLogs) => {
            if (log.original && log.new) {
                const diff = difference(log.original, log.new);
                const logOriginal = flattenObject(diff.original);
                const logNew = flattenObject(diff.new);
                Object.keys(logOriginal).forEach((key) => {
                    if (key === 'linked_order') {
                        key = 'linked_order.id';
                    }

                    if (!key.endsWith('__typename') && !key.endsWith('admin_id')) {
                        rows.push({
                            createdAt: (log.created_at) ? log.created_at : null,
                            modifiedKey: key,
                            modifiedField: this.label(`history.${key}`),
                            modifiedFrom: logOriginal[key],
                            modifiedTo: logNew[key],
                            modifiedBy: log.modified_by_erp ?
                                `ERP - ${log.erp_admin_name}` :
                                null,
                            modifierAdmin: log.new!.admin_id,
                        });
                    }
                });
            }
        });

        return (rows.length > 0) ? rows : null;
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.order.${id}` });
    }

    private renderValue(key: string, value: any): string | JSX.Element {
        if (value && key === 'linked_order.id') {
            return <a href={`${Paths.order}/${value}`}>{value}</a>;
        }

        if (typeof value === "boolean") {
            return this.props.intl.formatMessage({id: `common.${value ? 'yes' : 'no'}`});
        }
        if (isNull(value) || typeof value === 'undefined') {
            return this.props.intl.formatMessage({id: 'common.null'});
        }
        return value.toString();
    }

    public render() {
        return (this.state.history && this.state.history.length > 0) ? (
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>{this.label(`history.created_at`)}</th>
                        <th>{this.label(`history.modified_field`)}</th>
                        <th>{this.label(`history.modified_from`)}</th>
                        <th>{this.label(`history.modified_to`)}</th>
                        <th>{this.label(`history.modified_by`)}</th>
                    </tr>
                </thead>
                <tbody>
                    {(this.state.history && this.state.history.length > 0) && this.state.history.map((row: HistoryType, index: number): JSX.Element => {
                        return (
                            <tr
                                className="row historyRow"
                                key={`${index}`}
                                style={{ marginBottom: '5px' }}
                            >
                                <td>{row.createdAt}</td>
                                <td>{row.modifiedField}</td>
                                <td>{this.renderValue(row.modifiedKey, row.modifiedFrom)}</td>
                                <td>{this.renderValue(row.modifiedKey, row.modifiedTo)}</td>
                                <td>{
                                    (row.modifiedBy) ?
                                    row.modifiedBy :
                                    (row.modifierAdmin ?
                                        <AdminUserLink userId={row.modifierAdmin} /> :
                                        this.label('history.modified_by_user')
                                    )
                                }</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        ) : (this.state.loading) ? <Loading /> : (<h4>{this.label('history.no_data')}</h4>);
    }
}

export default compose(
    injectIntl,
)(History);



// WEBPACK FOOTER //
// ./src/modules/Order/components/History.tsx